/**
 * Forgot_Password_Screen.tsx — SK Platform
 * Premium, animated email-entry → forgotPassword() service. Endpoint resolved
 * only by api-endpoints (no URLs here). Matches the login/signup shell.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  Animated,
} from 'react-native';

import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';

import EmailComponent from '../../components/emailInput';
import PrimaryButton from '../../components/primaryButton';

import { forgotPassword } from '../../services/auth/auth';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useToast } from '../../components/toast';

import { getForgotDraft, setForgotDraft, clearForgotDraft } from './forgotDraft';
import { onResetOpened } from '../../navigation/authChannel';
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Forgot_Password_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'forgotPassword'>>();
  const toast = useToast();

  const savedEmail = getForgotDraft();
  const [email, setEmail] = useState(savedEmail ?? '');
  const [emailValid, setEmailValid] = useState(EMAIL_RE.test(savedEmail ?? ''));
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Persist the typed email across navigation.
  useEffect(() => {
    setForgotDraft(email);
  }, [email]);

  // When the emailed reset link opens the reset screen (usually a NEW tab, same
  // origin), advance THIS tab to the reset-password screen too — mirrors how the
  // "check your email" screen advances to login on verification.
  const advancedRef = useRef(false);
  useEffect(
    () =>
      onResetOpened((token) => {
        if (advancedRef.current) return;
        advancedRef.current = true;
        navigation.navigate('resetPassword', { token });
      }),
    [navigation],
  );

  // Card entrance animation (fade + rise).
  const fade = useRef(new Animated.Value(0)).current;
  const riseY = useRef(new Animated.Value(18)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 320, useNativeDriver: true }),
      Animated.spring(riseY, { toValue: 0, friction: 8, tension: 60, useNativeDriver: true }),
    ]).start();
  }, [fade, riseY]);

  const handleSubmit = async () => {
    if (isLoading || submitted) return;
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!emailValid) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const res = await forgotPassword(email);
      setSubmitted(true);
      clearForgotDraft();
      showSuccess(res, 'Email sent', res.message);
    } catch (err) {
      handleApiError(err, 'Failed', 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          <Animated.View
            style={[
              styles.card,
              { backgroundColor: colors.card, borderColor: colors.line, opacity: fade, transform: [{ translateY: riseY }] },
            ]}
          >
            <AuthBrandHeader />

            <Text style={[styles.title, { color: colors.ink }]}>Forgot password?</Text>
            <Text style={[styles.sub, { color: colors.ink2 }]}>
              Enter the email for your account and we'll send a secure link to reset your password.
            </Text>

            <View style={styles.form}>
              <EmailComponent
                value={email}
                onValueChange={(val, valid) => {
                  setEmail(val);
                  setEmailValid(valid);
                }}
                onSubmitEditing={handleSubmit}
              />

              {submitted && (
                <View style={[styles.successBox, { backgroundColor: colors.successSoft, borderColor: colors.successLine }]}>
                  <Text style={[styles.successText, { color: colors.success }]}>
                    Reset link sent to your email — check your inbox (and spam folder). The link
                    expires in 30 minutes.
                  </Text>
                </View>
              )}

              <PrimaryButton
                title={submitted ? 'Link sent  ✓' : isLoading ? 'Sending…' : 'Send reset link  →'}
                onPress={handleSubmit}
                loading={isLoading}
                hoverLift
                style={styles.submitBtn}
                textStyle={styles.submitBtnText}
              />

              {/* Premium divider + back-to-login link */}
              <View style={styles.footerDivider}>
                <View style={[styles.footerRule, { backgroundColor: colors.line }]} />
                <Text style={[styles.footerDividerText, { color: colors.ink4 }]}>REMEMBERED IT?</Text>
                <View style={[styles.footerRule, { backgroundColor: colors.line }]} />
              </View>
              <View style={styles.footer}>
                <Text style={[styles.footerText, { color: colors.ink3 }]}>Back to </Text>
                <PrimaryButton
                  title="Sign in  →"
                  onPress={() => navigation.navigate('login')}
                  variant="ghost"
                  size="small"
                  style={styles.footerLinkBtn}
                  textStyle={[styles.footerLinkText, { color: colors.accent }]}
                />
              </View>
            </View>
          </Animated.View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 24px 60px -18px rgba(15,23,42,0.22), 0 6px 18px -8px rgba(15,23,42,0.10)' } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 14 },
          shadowOpacity: 0.14,
          shadowRadius: 32,
          elevation: 10,
        }),
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -1,
    lineHeight: 38,
  },
  sub: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 28,
    textAlign: 'center',
    lineHeight: 22,
  },
  form: { width: '100%' },
  successBox: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginTop: 4,
    marginBottom: 8,
  },
  successText: { fontSize: 13, lineHeight: 20, fontWeight: '500' },
  submitBtn: {
    marginTop: 8,
    height: 52,
    minHeight: 52,
    borderRadius: 14,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 14px 28px rgba(11,11,13,0.32)' } as any)
      : { shadowColor: '#0B0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.32, shadowRadius: 22, elevation: 10 }),
  },
  submitBtnText: { fontSize: 15, letterSpacing: 1.2 },
  footerDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 14,
    gap: 12,
  },
  footerRule: { flex: 1, height: 1 },
  footerDividerText: { fontSize: 10.5, fontWeight: '800', letterSpacing: 1.2 },
  footer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  footerText: { fontSize: 14, fontWeight: '500' },
  footerLinkBtn: { width: 'auto' as any, minHeight: 'auto' as any, paddingHorizontal: 0 },
  footerLinkText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 14,
    letterSpacing: 0,
  },
});

export default Forgot_Password_Screen;
